import React, { useState } from 'react';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { FaTrash, FaHistory, FaSpinner, FaCheck, FaTimes, FaCalendarTimes } from 'react-icons/fa';
import './other_board.css';
import { functions } from '../../../../firebase/config';
import { httpsCallable } from 'firebase/functions';

const OtherBoard = () => {
  // # State for tracking function calls and confirmation dialogs
  const [loading, setLoading] = useState({
    pastMeetings: false
  });
  const [results, setResults] = useState({
    pastMeetings: null
  });
  const [showConfirm, setShowConfirm] = useState({
    pastMeetings: false
  });
  const [settings, setSettings] = useState({
    cutoffDays: 14,
    dryRun: false
  });

  // # Handle confirmation dialog
  const handleConfirmOpen = (type) => {
    setShowConfirm(prev => ({ ...prev, [type]: true }));
  };

  const handleConfirmClose = (type) => {
    setShowConfirm(prev => ({ ...prev, [type]: false }));
  };

  // # Handle setting changes
  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({ ...prev, [setting]: value }));
  };

  // # Delete old meetings implementation
  const handleCleanup = async (type) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    setResults(prev => ({ ...prev, [type]: null }));
    setShowConfirm(prev => ({ ...prev, [type]: false }));
    
    try {
      if (type === 'pastMeetings') {
        // # Call the deleteOldMeetings function
        const deleteOldMeetings = httpsCallable(functions, 'manualDeleteOldMeetings');
        
        // # Set params based on current settings
        const params = {
          cutoffDays: settings.cutoffDays,
          dryRun: settings.dryRun
        };
        
        // # Call the function and wait for result
        const result = await deleteOldMeetings(params);
        
        // # Process successful response
        setResults(prev => ({ 
          ...prev, 
          [type]: { 
            success: result.data.success,
            message: result.data.success 
              ? `${settings.dryRun ? 'Dry run completed' : 'Successfully deleted'} ${result.data.stats.meetingsDeleted} old meetings, processed ${result.data.stats.totalMeetingsProcessed} meetings in total.`
              : result.data.error || 'Failed to delete old meetings',
            timestamp: result.data.timestamp || new Date().toISOString(),
            stats: result.data.stats || {}
          }
        }));
      }
    } catch (error) {
      console.error(`Error executing ${type} cleanup:`, error);
      setResults(prev => ({ ...prev, [type]: { 
        success: false,
        message: error.message || 'An error occurred',
        timestamp: new Date().toISOString()
      }}));
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // # Render result status
  const renderResult = (type) => {
    const result = results[type];
    
    if (!result) return null;
    
    return (
      <div className={`tech_othr_result ${result.success ? 'tech_othr_success' : 'tech_othr_error'}`}>
        <div className="tech_othr_result-icon">
          {result.success ? <FaCheck /> : <FaTimes />}
        </div>
        <div className="tech_othr_result-content">
          <p className="tech_othr_result-message">{result.message}</p>
          <p className="tech_othr_result-timestamp">
            {new Date(result.timestamp).toLocaleString()}
          </p>
          {result.stats && (
            <div className="tech_othr_result-stats">
              <h4 className="tech_othr_stats-title">Operation Statistics:</h4>
              <ul className="tech_othr_stats-list">
                <li>Meeting types processed: {result.stats.meetingTypesProcessed || 0}</li>
                <li>Total meetings checked: {result.stats.totalMeetingsProcessed || 0}</li>
                <li>Old meetings found: {result.stats.oldMeetingsFound || 0}</li>
                <li>Meetings deleted: {result.stats.meetingsDeleted || 0}</li>
                <li>Notifications removed: {result.stats.notificationsRemoved || 0}</li>
                <li>Errors encountered: {result.stats.errors || 0}</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  // # Render confirmation dialog
  const renderConfirmDialog = (type, title, description) => {
    if (!showConfirm[type]) return null;
    
    return (
      <div className="tech_othr_confirm-overlay">
        <div className="tech_othr_confirm-dialog">
          <h3>{title}</h3>
          <p>{description}</p>
          
          {type === 'pastMeetings' && (
            <div className="tech_othr_confirm-settings">
              <div className="tech_othr_setting">
                <label htmlFor="cutoffDays">Delete meetings older than (days):</label>
                <input 
                  type="number" 
                  id="cutoffDays"
                  value={settings.cutoffDays}
                  min="1"
                  max="90"
                  onChange={(e) => handleSettingChange('cutoffDays', parseInt(e.target.value))}
                />
              </div>
              
              <div className="tech_othr_setting tech_othr_checkbox">
                <input 
                  type="checkbox" 
                  id="dryRun"
                  checked={settings.dryRun}
                  onChange={(e) => handleSettingChange('dryRun', e.target.checked)}
                />
                <label htmlFor="dryRun">
                  Dry run (only check which meetings would be deleted without actually deleting them)
                </label>
              </div>
            </div>
          )}
          
          <div className="tech_othr_confirm-actions">
            <button 
              className="tech_othr_confirm-cancel" 
              onClick={() => handleConfirmClose(type)}
            >
              Cancel
            </button>
            <button 
              className="tech_othr_confirm-proceed" 
              onClick={() => handleCleanup(type)}
            >
              {settings.dryRun ? 'Run Check' : 'Proceed with Deletion'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="tech_othr_container">
      <SharedHeader />
      
      <main className="tech_othr_content">
        <div className="tech_othr_header">
          <h1>Other Technical Functions</h1>
          <p>Manage additional technical operations and system maintenance</p>
        </div>

        <div className="tech_othr_cards">
          {/* Delete Past Meetings Card */}
          <div className="tech_othr_card">
            <div className="tech_othr_card-header">
              <FaCalendarTimes className="tech_othr_card-icon" />
              <h2>Delete Past Meetings</h2>
            </div>
            <div className="tech_othr_card-body">
              <p>
                Removes all past meetings from the database that are older than the specified cutoff period.
                This helps maintain database efficiency and reduces storage costs.
              </p>
              <p className="tech_othr_card-note">
                This operation cannot be undone. All meeting data will be permanently removed.
              </p>
              <p className="tech_othr_card-info">
                <strong>Scheduled run:</strong> Every Sunday at 7:00 PM automatically removes meetings older than 14 days.
              </p>
              {renderResult('pastMeetings')}
            </div>
            <div className="tech_othr_card-footer">
              <button 
                className="tech_othr_button"
                onClick={() => handleConfirmOpen('pastMeetings')}
                disabled={loading.pastMeetings}
              >
                {loading.pastMeetings ? <FaSpinner className="tech_othr_spinner" /> : 'Delete Past Meetings'}
              </button>
            </div>
          </div>

          {/* Additional cards can be added here */}
        </div>
      </main>

      <DashboardFooter />

      {/* Confirmation Dialogs */}
      {renderConfirmDialog(
        'pastMeetings',
        'Confirm Delete Past Meetings',
        'This will permanently delete all meetings that occurred in the past beyond the specified retention period. This operation cannot be undone and all meeting data will be lost. Are you sure you want to proceed?'
      )}
    </div>
  );
};

export default OtherBoard;
